import CurrentUserDetail from '../model/currentuser/CurrentUserDetail';
import StadiumMaterialuEnum from '../model/currentuser/StadiumMaterialuEnum';
import { LegislationProcessInfo, Organizacia } from '../model/currentuser/LegislationProcessInfo';
import ClassType from '../model/pripomienka/ClassType';
import UserRole from '../model/currentuser/UserRole';
import TypPripomienky from '../model/pripomienka/TypPripomienky';
import PripomienkaDetail from '../model/pripomienka/PripomienkaDetail';
import SecuredPageErrorConditions from '../model/SecuredPageErrorConditions';
import Uuid from '../model/Uuid';

export const canSendErrorConditions = (pouzivatel: CurrentUserDetail, legislationProcess: LegislationProcessInfo):
    SecuredPageErrorConditions => ({
  title: 'Nie je možné odoslať pripomienky',
  conditions: [{
    condition: pouzivatel && !pouzivatel.isOdoslaneBezPripomienok,
    message: 'Pripomienky nie je možné odoslať, vaša organizácia sa už vyjadrila spôsobom "bez pripomienok".',
  }, {
    condition: processIsInMpk(legislationProcess),
    message: 'Pripomienky nie je možné odoslať, aktuálne štádium materiálu nie je "Pripomienkové konanie".',
  }],
});

export const canAddErrorConditions = (pouzivatel: CurrentUserDetail, legislationProcess: LegislationProcessInfo):
    SecuredPageErrorConditions => ({
  title: 'Nie je možné pridať pripomienku',
  conditions: [{
    condition: pouzivatel && !pouzivatel.isOdoslaneBezPripomienok,
    message: 'Aktuálna organizácia už je subjektom bez pripomienok. Nie je možné pridať novú pripomienku.',
  }, {
    condition: processIsInMpk(legislationProcess),
    message: 'Pripomienku nie je možné pridať, aktuálne štádium materiálu nie je "Pripomienkové konanie".',
  }],
});

export const canEditErrorConditions = (legislationProcess: LegislationProcessInfo):
    SecuredPageErrorConditions => ({
  title: 'Nie je možné upraviť pripomienku',
  conditions: [{
    condition: processIsInMpk(legislationProcess),
    message: 'Pripomienku nie je možné upraviť, aktuálne štádium materiálu nie je "Pripomienkové konanie".',
  }],
});

export const canSendOrAdd = (pouzivatel: CurrentUserDetail, legislationProcess: LegislationProcessInfo):
    boolean => pouzivatel && !pouzivatel.isOdoslaneBezPripomienok && processIsInMpk(legislationProcess);

export const canSend = (pouzivatel: CurrentUserDetail):
    boolean => pouzivatel && (pouzivatel.isPovinnyPripomienkovatel || pouzivatel.isDalsiPripomienkovatel)
    && ((!isNelegOrg(pouzivatel) && isOdosielatel(pouzivatel)) || isNelegOrg(pouzivatel));

export const isOdosielatel = (pouzivatel: CurrentUserDetail):
    boolean => pouzivatel.opravnenia.includes(UserRole.ODOSIELATEL);

export const isNelegOrg = (pouzivatel: CurrentUserDetail):
    boolean => pouzivatel.opravnenia.includes(UserRole.NELEGISLATIVNA_ORGANIZACIA);

export const canSendWithoutPripomienky = (
  pouzivatel: CurrentUserDetail,
  legislationProcess: LegislationProcessInfo,
): boolean => canSendOrAdd(pouzivatel, legislationProcess)
    && canSend(pouzivatel) && pouzivatel.pripomienkaExistsForUserOrg === false;

export const processIsInMpk = (legislationProcess: LegislationProcessInfo): boolean => legislationProcess
    && legislationProcess.stadiumProcesu === StadiumMaterialuEnum.MPK;

export const processInCorrectStates = (legislationProcess: LegislationProcessInfo): boolean => legislationProcess
    && (legislationProcess.stadiumProcesu === StadiumMaterialuEnum.MPK
        || legislationProcess.stadiumProcesu === StadiumMaterialuEnum.VYHODNOTENIE_MPK
        || legislationProcess.stadiumProcesu === StadiumMaterialuEnum.VYHODNOTENIE_MPK_AKTY);

export const canRemove = (
  pouzivatel: CurrentUserDetail,
  legislationProcess: LegislationProcessInfo,
  classType: ClassType,
  pripomienka: PripomienkaDetail,
): boolean => pouzivatel && legislationProcess && (pouzivatel.opravnenia.includes(UserRole.SUPERADMIN)
    || (processIsInMpk(legislationProcess)
        && classType === ClassType.PRIPOMIENKA && pripomienka.odoslana === false
        && pripomienka.pripomienkovatel.uuid === pouzivatel.uuid
        && pripomienka.pripomienkujuciSubjekt
        && pouzivatel.organizaciaUuid === pripomienka.pripomienkujuciSubjekt.uuid));

export const canEdit = (
  pouzivatel: CurrentUserDetail,
  legislationProcess: LegislationProcessInfo,
  pripomienka: PripomienkaDetail,
): boolean => pripomienka && pripomienka.typ !== TypPripomienky.HROMADNA && pripomienka.odoslana === false
    && pripomienka.pripomienkovatel && pouzivatel.uuid === pripomienka.pripomienkovatel.uuid
    && pripomienka.pripomienkujuciSubjekt
    && pouzivatel.organizaciaUuid === pripomienka.pripomienkujuciSubjekt.uuid
    && processIsInMpk(legislationProcess);

export const canRemoveSubjektBezPripomienok = (
  pouzivatel: CurrentUserDetail,
  legislationProcess: LegislationProcessInfo,
  subjektBezPripomienokId: string,
): boolean => (pouzivatel && pouzivatel.opravnenia.includes(UserRole.SUPERADMIN))
    || (processIsInMpk(legislationProcess) && pouzivatel
        && pouzivatel.organizaciaUuid === subjektBezPripomienokId
        && pouzivatel.opravnenia.includes(UserRole.ODOSIELATEL));

export const canExportVznesenePripomienky = (
  pouzivatel: CurrentUserDetail,
  legislationProcess: LegislationProcessInfo,
): boolean => (legislationProcess
        && ((legislationProcess.typCode === 'AktEU'
                && pouzivatel.opravnenia.includes(UserRole.AKTY_EU)
                && (isGestor(pouzivatel, legislationProcess) || isSpolugestor(pouzivatel, legislationProcess)))
            || (legislationProcess.typCode !== 'AktEU'
                && isAktualnyPredkladatelInAktPredkInstitucia(pouzivatel, legislationProcess))))
    || pouzivatel.opravnenia.includes(UserRole.SUPERADMIN);

export function isAktualnyPredkladatelInAktPredkInstitucia(
  pouzivatel: CurrentUserDetail,
  legislationProcess: LegislationProcessInfo,
): boolean {
  return isAktualnaInstitucia(pouzivatel, legislationProcess) && isAktualnyPredkladatel(pouzivatel, legislationProcess);
}

// eslint-disable-next-line max-len
export function isAktualnyPredkladatel(pouzivatel: CurrentUserDetail, legislationProcess: LegislationProcessInfo): boolean {
  return legislationProcess && legislationProcess.aktualnyPredkladatel
      && legislationProcess.aktualnyPredkladatel?.uuid === pouzivatel?.uuid;
}

export const isAdmin = (
  pouzivatel: CurrentUserDetail | undefined,
): boolean => (
  pouzivatel !== null && pouzivatel?.opravnenia?.includes(UserRole.SUPERADMIN));

function isGestor(pouzivatel: CurrentUserDetail, legislationProcess: LegislationProcessInfo): boolean {
  return legislationProcess && legislationProcess.gestor
      && legislationProcess.gestor.ldapUuid === pouzivatel.organizaciaUuid;
}

function isSpolugestor(pouzivatel: CurrentUserDetail, legislationProcess: LegislationProcessInfo): boolean {
  return legislationProcess && legislationProcess.spolugestorList
      && includesOrganizationUuid(legislationProcess.spolugestorList, pouzivatel.organizaciaUuid);
}

function isAktualnaInstitucia(pouzivatel: CurrentUserDetail, legislationProcess: LegislationProcessInfo): boolean {
  return legislationProcess && legislationProcess.aktualnaInstitucia
      && legislationProcess.aktualnaInstitucia.ldapUuid === pouzivatel.organizaciaUuid;
}

function includesOrganizationUuid(organizationList: Organizacia[], organizaciaUuid: Uuid): boolean {
  return organizationList.find((organization) => organization.ldapUuid === organizaciaUuid) != null;
}
